/* Hybird storefront motion (hybird-animator rules: calm, 150/240ms, ease-out, reduced-motion safe).
   - Reveals sections as they enter the viewport.
   - Feeds scroll progress (--hy-p, 0..1) to [data-hy-progress] elements.
   - Drives the sticky product story ([data-hy-story]).
   Content is fully visible without JS; motion is only added when the visitor allows it. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;

  /* Sticky story: always active (it changes which product is shown, not just decoration). */
  function bindStory(scope) {
    scope.querySelectorAll('[data-hy-story]:not([data-hy-bound])').forEach((story) => {
      story.dataset.hyBound = '';
      const steps = [...story.querySelectorAll('[data-hy-step]')];
      const frames = [...story.querySelectorAll('[data-hy-frame]')];
      const dots = [...story.querySelectorAll('[data-hy-dot]')];
      const activate = (index) => {
        [steps, frames, dots].forEach((list) =>
          list.forEach((el, i) => el.classList.toggle('is-active', i === index))
        );
      };
      activate(0);
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) activate(steps.indexOf(entry.target));
          });
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      steps.forEach((step) => observer.observe(step));
    });
  }

  /* Scroll progress: data-hy-progress="exit" (0 at top, 1 when scrolled past) or "through" (enter to leave). */
  const progressEls = new Set();
  let ticking = false;
  function updateProgress() {
    ticking = false;
    const vh = window.innerHeight;
    progressEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      let p;
      if (el.dataset.hyProgress === 'exit') {
        p = -rect.top / Math.max(rect.height * 0.6, 1);
      } else {
        p = (vh * 0.85 - rect.top) / Math.max(rect.height, 1);
      }
      el.style.setProperty('--hy-p', Math.min(Math.max(p, 0), 1).toFixed(4));
    });
  }
  function requestProgress() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateProgress);
    }
  }

  /* Section reveal. */
  let revealObserver;
  function bindReveal(scope) {
    if (!revealObserver) return;
    const targets = scope.querySelectorAll(
      '#MainContent > .shopify-section:not(:first-child), [data-hy-reveal]'
    );
    targets.forEach((el) => {
      if (el.hasAttribute('data-hy-revealed-bound')) return;
      el.setAttribute('data-hy-revealed-bound', '');
      el.classList.add('hy-reveal');
      revealObserver.observe(el);
    });
  }

  function bindProgress(scope) {
    scope.querySelectorAll('[data-hy-progress]').forEach((el) => progressEls.add(el));
    requestProgress();
  }

  function init(scope = document) {
    bindStory(scope);
    if (reduceMotion.matches) return;
    bindProgress(scope);
    bindReveal(scope);
  }

  if (!reduceMotion.matches) {
    root.classList.add('hy-motion');
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );
    window.addEventListener('scroll', requestProgress, { passive: true });
    window.addEventListener('resize', requestProgress);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* Theme editor: re-bind when a section is added or reloaded, and never hide content there. */
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    event.target.querySelectorAll('.hy-reveal').forEach((el) => el.classList.add('is-revealed'));
  });
})();

/* Panel check (snippets/hybird-panel-check.liquid): while the cart has breakers but no Controller or
   PSU, Checkout in that summary points to the reminder until the installer adds the part or ticks
   "I already have …". Delegated, so it keeps working after the cart re-renders. */
document.addEventListener(
  'click',
  (event) => {
    // Card checkout and the WSH "Net Orders Checkout" (net terms) button both pass through the check.
    const button = event.target.closest('[name="checkout"], #wcp-30-open');
    const confirm = button?.closest('.cart__ctas')?.querySelector('[data-hy-panel-confirm]');
    if (!confirm || confirm.checked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const box = confirm.closest('.hy-panel-check');
    box.classList.remove('is-flagged');
    void box.offsetWidth;
    box.classList.add('is-flagged');
    box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    confirm.focus({ preventScroll: true });
  },
  true
);

/* Application received (snippets/hybird-received.liquid): wpd_custom calls this when the WSH sign-up
   form reports success; ?received=1 shows the same state on reload or for previewing. */
window.hyShowReceived = () => {
  const panel = document.querySelector('.hy-rcv');
  if (!panel) return;
  document.documentElement.classList.add('hy-received');
  window.scrollTo(0, 0);
  if (!/[?&]received=1/.test(location.search)) history.replaceState(null, '', location.pathname + '?received=1');
  panel.querySelector('h1')?.focus({ preventScroll: true });
};
if (/[?&]received=1/.test(location.search)) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => window.hyShowReceived());
  else window.hyShowReceived();
}
